import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NOTE_SCAN_DIALOG_CONFIG } from '../../../../shared/config/floating-ai-dialog.config';
import { TaskDialogComponent, TaskDialogData, TaskDialogResult } from '../../../../shared/components/task-dialog/task-dialog.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { EMPTY, Subscription, combineLatest, of, switchMap, take, takeUntil } from 'rxjs';
import { FileObject, mapMediaToFileObject } from 'src/app/core/interfaces';
import { editedValues, restoreEdits } from 'src/app/core/utils/form-edits';
import { withUnsavedMedia } from './note-media';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { patientGroupId } from './../../../../core/utils/patient-group';
import { FIRST_CONSULT_INPATIENT_CODE_SUGGESTIONS, FIRST_CONSULT_OPD_CODE_SUGGESTIONS, FOLLOW_UP_CONSULT_INPATIENT_CODE_SUGGESTIONS, FOLLOW_UP_CONSULT_OPD_CODE_SUGGESTIONS } from './../../../../core/data/app.data';
import { AIService } from './../../../../core/services/ai.service';
import { AnalyticsService } from './../../../../core/services/analytics.service';
import { ProcedureCode } from './../../../../core/services/app-data.service';
import { CodingBundleService } from './../../../../core/services/coding-bundle.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { NoteTypeService } from './../../../../core/services/note-type.service';
import { PatientAppointmentService } from './../../../../core/services/patient-appointment.service';
import { PatientNoteService } from './../../../../core/services/patient-note.service';
import { PatientService } from './../../../../core/services/patient.service';
import { TokenUsageService } from './../../../../core/services/token-usage.service';
import { ProcedureCodeSelectorComponent } from './../../../../shared/components/procedure-code-selector/procedure-code-selector.component';
import * as _ from 'lodash';
// ── Risk management (workstream F of tasks/risk-management.md) ────────────────────────────
import { HospitalService } from './../../../../core/services/hospital.service';
import { PatientRiskItemService } from './../../../../core/services/patient-risk-item.service';
import { RiskItemAddDialogComponent, RiskItemAddDialogData, RiskItemAddDialogResult } from './../../../../shared/components/risk-suggestion-list/risk-item-add-dialog.component';
import { RiskSuggestion, normaliseRiskSuggestions, pickEncounterAppointment, predictRiskFacility, riskResolutionLabel } from './../../../../shared/components/risk-suggestion-list/risk-suggestion.model';
// F3 / E3-1 — admission history is what lets §4 branch 3 run in the browser, and branch 3 is THE
// branch that matters for a ward round: the patient is admitted, and the hospital is on the
// admission record rather than on any appointment.
import { PatientAdmissionHistoryService } from './../../../../core/services/patient-admission-history.service';
// ── Ward-round background detection (workstream M of tasks/risk-management.md, Phase 2) ────
// The note now renders the results of a detection pass that is STILL RUNNING when the doctor
// arrives, so it needs the request row (M7) and the live update stream (M6) — and, for M3/M8,
// the same task service every other suggested-task surface writes through.
import { PatientAiService } from './../../../../core/services/patient-ai.service';
import { PatientTaskService } from './../../../../core/services/patient-task.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import { ChatService } from './../../../../chat/chat.service';
import { chatNoteCard, noteCardTitle, notePrefill, withChatText } from '../chat-action-request';



@Component({
  selector: 'app-patient-note-detail',
  templateUrl: './patient-note-detail.component.html',
  styleUrls: ['./patient-note-detail.component.scss']
})
export class PatientNoteDetailComponent extends BaseComponent implements OnInit, OnDestroy {
  todayDate: moment.Moment = moment();
  @ViewChild('procedureCodeSelector') procedureCodeSelector!: ProcedureCodeSelectorComponent;
  @ViewChild('scanNoteDetailsDialog') scanNoteDetailsDialog?: TemplateRef<any>;
  /** M8 — the shared Task Details dialog, same template as `discharge-details`. */

  form!: FormGroup;
  editingNote: any;
  /** The conversation a chat action opened this page from, or null. */
  chatConversationId: string | null = null;
  /** The text the chat sent, kept above a note type's template when one is picked. */
  private chatNoteText = '';
  /**
   * Recording job id carried on the incoming note (ward round hand-off or an existing note).
   * Not a form control on purpose - it is invisible plumbing - so it is captured here when
   * editingNote arrives and merged back into the save payload. Null when there is no recording.
   */
  private recordingJobId: string | null = null;
  /**
   * F1 — the `AIRequest` this note came from.
   *
   * Ward rounds create an `AIRequest` now (D1), and `ai-dialog`'s close path puts its id on the
   * editing note. Captured off-form for exactly the reason `recordingJobId` is: `patchForm` uses
   * `form.patchValue`, which only populates controls that already exist, so a value with no
   * control is silently dropped. It is merged back into the save payload AND stamped onto every
   * risk item confirmed from this note (F3), which is what gives a ward-round issue provenance.
   */
  private aiRequestId: string | null = null;

  // ══ M5-M8 — BACKGROUND DETECTION STATE ═══════════════════════════════════════════════════
  //
  // The ward-round pipeline sends completion BEFORE it runs detection, so the doctor lands on
  // this screen while the detectors are still talking to the model. Everything below exists to
  // render that honestly: a loader while it runs, the results when they arrive, and an empty
  // state that is only ever shown once the pass has actually finished (D14).

  /**
   * D14 — `none | pending | complete | failed`, read from `AIRequests.backgroundDetectionStatus`.
   *
   * NEVER inferred. `suggestedRiskItems` being NULL means "still running" and "finished, found
   * nothing" equally, and those two must not look the same on screen; that ambiguity is the
   * entire reason the column exists.
   *
   * Defaults to `'none'`, which renders no loader and no empty state — the right answer for a
   * hand-written note and for every request predating the column.
   */
  backgroundDetectionStatus: string = 'none';

  /**
   * M3 — the admin tasks the ward-round pipeline now detects, in the shape the column holds:
   * `{ tasks: [ { task, score, reason } ] }`, the same shape `patient-ai-request-detail` and
   * `discharge-details` render. NULL until detection lands, and NULL permanently when it found
   * nothing — which is why it can only be read alongside `backgroundDetectionStatus`.
   */
  suggestedAdminTasks: any = null;

  /** M8 — the patient's existing tasks, for `canAddTask`'s dedupe. */
  patientTasks: any[] = [];

  /** The task dialog's working copy, mirroring `discharge-details` / `patient-ai-request-detail`. */


  /**
   * Guards the M7 fetch against the M6 socket stream.
   *
   * Both write the same fields. The fetch is a SNAPSHOT taken when the note opened; a socket
   * update is always newer than any snapshot in flight. Bumped on every applied socket update, so
   * a fetch that returns late can tell its answer is stale and stand down rather than reinstating
   * `pending` over a `complete` that has already arrived — which would put the spinner back
   * underneath results the doctor can already see.
   */
  private detectionUpdateSeq = 0;

  /** The request id the M7 fetch has already been issued for, so `patchForm` does not refetch. */
  private detectionFetchedFor: string | null = null;
  /**
   * F1/F2 — the extractor's suggested facility issues, carried in memory from the ward-round
   * hand-off. NEVER loaded from the note: `PatientNote` has no such column and none is wanted.
   * Empty for every other note, which is what makes F2 naturally ward-round-only.
   */
  riskSuggestions: RiskSuggestion[] = [];
  /** F3 — the patient's confirmed items, for the dedupe guard. */
  riskItems: any[] = [];
  /** F3 — hospitals with wards, to name the facility in the confirm dialog. */
  hospitals: any[] = [];
  /**
   * F3 / E3-1 — this patient's admission records, for the facility preview only.
   *
   * A ward round is by definition an inpatient encounter, so this is the branch that decides the
   * facility on nearly every item added from this screen. Read-only; the server still resolves
   * what is stored.
   */
  admissionRecords: any[] = [];
  /** Guards double-submits while a create is in flight. */
  addingRiskItem: boolean = false;
  patient: any = {};
  groupId!: string;
  noteTypes: any[] = [];
  codingBundles: any[] = [];
  appointmentType: string = '';
  noteMediaObjects: FileObject[] = [];
  loadedNoteMediaObjects: FileObject[] = [];
  /** The note whose media is on the page, so a patch of another note does not inherit this one's unsaved files. */
  private mediaPatchedFor: string | null = null;

  selectedProcedureCodes: ProcedureCode[] = [];
  suggestedProcedureCodes: ProcedureCode[] = [];

  private routeParamsSub!: Subscription;

  tokenBalance?: number;
  lockProcessing: boolean = false;
  isPremium: boolean = false;

  /** A state unit (tasks/state-mode.md) records ICD-10 codes only. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService,
    private patientService: PatientService,
    private noteService: PatientNoteService,
    private noteTypeService: NoteTypeService,
    private codingBundleService: CodingBundleService,
    private patientAppointmentService: PatientAppointmentService,
    private aiService: AIService,
    private usageService: TokenUsageService,
    private cd: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
    // Workstream F — the patient-scoped risk service (J1) and the hospitals used only to NAME the
    // facility in the confirm dialog. §4 remains resolved server-side.
    private patientRiskItemService: PatientRiskItemService,
    private hospitalService: HospitalService,
    // E3-1 — read-only, for the §4 branch-3 preview. Already loaded for the open patient.
    private patientAdmissionHistoryService: PatientAdmissionHistoryService,
    // M6/M7 — the live `aiRequestUpdated` stream and the by-id fetch, and M8's task write path.
    private patientAiService: PatientAiService,
    private taskService: PatientTaskService,
    private chat: ChatService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.initForm();

    this.routeParamsSub = this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        switchMap(params => {
          const noteId = params.get('id');
          if (noteId) {
            // Use case 2: Edit an existing note
            return this.noteService.getNoteById(noteId)
          } else {
            // Use case 1 or 3: New note
            return of(null);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(note => {

        if (note && note.data) {
          // Use case 2: Patch the form with the retrieved note data
          this.editingNote = note.data;
          this.patchForm({ keepEdits: true });
        } else {

          // Use case 3: Check for patientID
          this.route.queryParams
            .pipe(
              takeUntil(this.destroy$),
              take(1)
            ).subscribe(params => {
              const patientId = params['patientId'];
              if (patientId) {
                // Use case 3: Patch the form with patientId
                setTimeout(() => {
                  this.form.patchValue({ patientId });
                })
              }
            });
        }
      });

    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.isPremium = selectedGroup.isPremium === true;
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();


    this.patientService.currentPatient$
      .pipe(
        switchMap(selectedPatient => {
          if (selectedPatient) {
            this.patient = selectedPatient;
            this.setSuggestedCodes();
            this.form.patchValue({ patientId: this.patient.id, groupId: patientGroupId(this.patient, this.groupId) });
          } /* else {
            this.returnToParent();
          } */
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.noteService.editingNote$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(note => {
        if (note) {
          this.editingNote = note;
          this.patchForm({ keepEdits: true });
        } else {
          this.resetForm()
        }

      });

    this.codingBundleService.codingBundles$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(bundles => {
        this.codingBundles = _.sortBy(bundles, 'label');;
      });

    // F3 — dedupe against what is already on the register for this patient.
    this.patientRiskItemService.patientRiskItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        if (items) {
          this.riskItems = items;
        }
      });

    // F3 — names the hospital/ward in the confirm dialog. Stays NULL on a failed fetch, by design.
    this.hospitalService.hospitals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hospitals => {
        if (hospitals) {
          this.hospitals = hospitals;
        }
      });

    // E3-1 — §4 branch 3 in the browser. This subject emits `[]` for "no admissions" AND for "the
    // fetch failed", so an empty list is never treated as proof of an in-rooms issue; the preview
    // stays provisional and the server has the last word.
    this.patientAdmissionHistoryService.patientAdmissionRecords$
      .pipe(takeUntil(this.destroy$))
      .subscribe(records => {
        this.admissionRecords = Array.isArray(records) ? records : [];
      });

    combineLatest([
      this.noteTypeService.noteTypes$,
      this.patientAppointmentService.patientAppointments$
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([noteTypes, appointments]) => {
      if (noteTypes) {
        this.noteTypes = this.updateNoteTypesStructure(noteTypes);
      }

      if (appointments && noteTypes) {
        this.matchAppointmentToNoteType(appointments);
      }
    });

    // M8 — dedupe feed for the SUGGESTED TASKS section, mirroring `discharge-details`.
    this.taskService.patientTasks$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tasks => {
        this.patientTasks = Array.isArray(tasks) ? tasks : [];
      });

    // ══ M6 — LIVE REFRESH ════════════════════════════════════════════════════════════════
    //
    // Subscribed HERE, in ngOnInit, and not where the note is patched: the subscription has to
    // be listening BEFORE the M7 fetch is issued, or an update landing in the gap between the
    // fetch leaving and returning is simply lost and the section stays on its loader forever.
    // The filter reads `this.aiRequestId` on every event rather than closing over it, so the
    // subscription survives the note being swapped underneath it.
    //
    // `aiRequestUpdates$` rather than `incrementalUpdates$`: the latter is gated on
    // `editingAIRequestSubject` and so only fires while the AI REQUEST DETAIL is open. A ward
    // round never opens that screen at all (B4), which is precisely why this stream was added.
    this.patientAiService.aiRequestUpdates$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update: any) => {
        if (!update || !this.aiRequestId || update.id !== this.aiRequestId) return;
        this.applyDetectionUpdate(update);
        // The socket callback is outside Angular's change detection on some transports; the
        // spinner-to-results swap must not wait for an unrelated event to repaint it.
        this.cd.detectChanges();
      });

    this.usageService.tokenBalance$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.tokenBalance = result;
      });

  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.routeParamsSub) {
      this.routeParamsSub.unsubscribe();
    }
  }

  private updateNoteTypesStructure(noteTypes: any[]): any[] {
    // normalize headings
    for (let nt of noteTypes) {
      if (this.noteTypeService.areHeadingsStrings(nt.headings)) {
        nt.headings = this.noteTypeService.convertHeadingsToObjects(nt.headings);
      }
    }
    // sort in-place by the label
    return noteTypes.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );
  }

  initForm() {
    this.form = this.fb.group({
      date: [moment(), Validators.required],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      patientId: [this.patient.id, Validators.required],
      noteType: [''],
      codingBundle: [''],
      noteTypeHeadings: [''],
      title: ['', Validators.required],
      notes: ['', Validators.required],
      isProcedure: [false],
      isBilling: [false],
      procedureCodes: [[]],
      paidCash: [false],
      noCharge: [false],
    });
  }

  get isProcedureNote(): boolean {
    const { isProcedure } = this.form.value;
    return isProcedure;
  }

  get isBillingNote(): boolean {
    const { isBilling } = this.form.value;
    return isBilling;
  }

  /**
   * Fills the form from `editingNote`. The note is filled from the timeline's copy and again when its fetch lands;
   * with `keepEdits`, fields typed in between stay as typed (not across notes).
   */
  patchForm({ keepEdits = false }: { keepEdits?: boolean } = {}) {
    if (!this.editingNote) {
      return; // Guard clause in case this.editingNote is not yet set
    }
    const otherNote = !!this.mediaPatchedFor && this.mediaPatchedFor !== this.editingNote.id;
    const edits = keepEdits && !otherNote ? editedValues(this.form) : {};
    if (otherNote) this.form.markAsPristine();
    // Basic patching for directly matched fields
    this.form.patchValue(this.editingNote);

    // patchValue only populates existing form controls, so capture this off-form value here.
    this.recordingJobId = this.editingNote.recordingJobId ?? null;

    // F1 — same reasoning, two more off-form values.
    //
    // `aiRequestId` reaches here from `ai-dialog`'s ward-round close path (D1/B6) and, for an
    // existing note, straight off the loaded row — so re-saving a note never drops the link it
    // already had.
    //
    // `suggestedRiskItems` is memory-only and arrives ALREADY FLATTENED to an array by
    // `ai-dialog.extractSuggestedRiskItems`. `normaliseRiskSuggestions` is used anyway because
    // the field is NULL far more often than it is populated, and because tolerating the
    // `{ items: [...] }` shape too costs nothing and removes an assumption about a caller.
    this.aiRequestId = this.editingNote.aiRequestId ?? null;
    this.riskSuggestions = normaliseRiskSuggestions(this.editingNote.suggestedRiskItems);

    // M7 — the note's own copy of the suggestions is whatever `ai-dialog` happened to be holding
    // when it closed, and for a ward round that is now ALWAYS empty: the pipeline sends
    // completion before detection runs, so the hand-off carries NULL by construction. The
    // request row is the only authority on both the suggestions and the status, so go and get it.
    this.loadBackgroundDetection();

    const noteType = this.editingNote.detectedNoteType;

    if (noteType) {
      this.form.patchValue({ noteTypeHeadings: noteType.headings, noteType: noteType.id, isProcedure: noteType.isProcedure, isBilling: noteType.isBilling || false });
    }


    // For any other special handling or transformations, continue below
    if (this.editingNote.date) {
      this.form.patchValue({ date: moment(this.editingNote.date) });
    }


    // The note's media, plus anything pasted or picked before this (re)patch: the note is patched once from the
    // timeline and again when its fetch lands, and that second patch used to drop a picture pasted in between.
    // Not across notes: opening another one starts from its own media.
    const unsaved = this.mediaPatchedFor && this.mediaPatchedFor !== this.editingNote.id ? [] : this.noteMediaObjects;
    this.mediaPatchedFor = this.editingNote.id;
    this.noteMediaObjects = withUnsavedMedia(this.editingNote.media ? mapMediaToFileObject(this.editingNote.media) : [], unsaved);
    this.loadedNoteMediaObjects = [...this.noteMediaObjects];
    restoreEdits(this.form, edits);

    this.selectedProcedureCodes = this.editingNote.procedureCodes || [];

  }

  setSuggestedCodes() {
    const { noteType } = this.form.value;
    if (!noteType) {
      this.suggestedProcedureCodes = [];
      return;
    }

    const status = this.patient.status;
    const noteLabel = noteType.toLowerCase();
    const isFirstConsult = noteLabel.includes("new") || noteLabel.includes("first");
    const isFollowUpConsult = noteLabel.includes("follow") || noteLabel.includes("repeat");

    const codeSuggestionsMap: any = {
      opd: {
        first: FIRST_CONSULT_OPD_CODE_SUGGESTIONS,
        follow: FOLLOW_UP_CONSULT_OPD_CODE_SUGGESTIONS
      },
      inpatient: {
        first: FIRST_CONSULT_INPATIENT_CODE_SUGGESTIONS,
        follow: FOLLOW_UP_CONSULT_INPATIENT_CODE_SUGGESTIONS
      }
    };

    let consultationType = '';
    if (isFirstConsult) {
      consultationType = 'first';
    } else if (isFollowUpConsult) {
      consultationType = 'follow';
    }

    if (consultationType && codeSuggestionsMap[status]) {
      this.suggestedProcedureCodes = codeSuggestionsMap[status][consultationType] || [];
    } else {
      this.suggestedProcedureCodes = [];
    }
  }


  matchAppointmentToNoteType(appointments: any[]) {


    const appointmentsToday = appointments.filter((appointment: any) => moment.utc(appointment.date).isSame(moment.utc(), 'day'));

    if (!appointmentsToday || !appointmentsToday.length) {
      return;
    }
    const noteTypes = this.noteTypes.filter((noteType: any) => noteType.label === appointmentsToday[0].appointmentType);

    if (noteTypes && noteTypes.length) {
      this.selectNoteType({ value: noteTypes[0] });
    }

  }

  selectNoteType(event: any) {
    const noteType = event.value;
    const selectedNoteType = this.noteTypes.filter(nt => nt.id === noteType)[0];
    let output = '';
    if (selectedNoteType.forAi) {
      for (let heading of selectedNoteType.headings) {
        output += `${heading.text}:\n`;
        for (let subheading of heading.subheadings) {
          output += `- ${subheading.text}:\n`;
        }
        output += `\n`;
      }
    } else {
      output = selectedNoteType.templateText;
    }

    setTimeout(() => {
      this.form.patchValue({ noteTypeHeadings: selectedNoteType.headings, noteType: selectedNoteType.id, isProcedure: selectedNoteType.isProcedure, isBilling: selectedNoteType.isBilling || false });
      this.form.patchValue({ notes: this.chatNoteText ? withChatText(output, this.chatNoteText) : output, title: selectedNoteType.label });
      this.setSuggestedCodes();
      this.cd.detectChanges();
    });
  }

  onIsProcedureToggle(event: any): void {
    if (event.checked && this.form.get('isBilling')?.value) {
      this.form.patchValue({ isBilling: false });
    }
  }

  onIsBillingToggle(event: any): void {
    if (event.checked && this.form.get('isProcedure')?.value) {
      this.form.patchValue({ isProcedure: false });
    }
  }

  selectCodingBundle(event: any) {
    const bundle = event.value;

    setTimeout(() => {
      if (bundle.procedureCodes) {
        this.selectedProcedureCodes = bundle.procedureCodes.map((c: any) => { return { ...c, paidCash: bundle.paidCash, noCharge: bundle.noCharge } });
        this.form.patchValue({ paidCash: bundle.paidCash, noCharge: bundle.noCharge });
      }
      this.cd.detectChanges();
    });
  }

  togglePaidCash(event: MatSlideToggleChange) {
    this.selectedProcedureCodes = this.selectedProcedureCodes.map((c: any) => { return { ...c, paidCash: event.checked } });
    this.form.patchValue({ procedureCodes: this.selectedProcedureCodes });
  }

  toggleNoCharge(event: MatSlideToggleChange) {
    this.selectedProcedureCodes = this.selectedProcedureCodes.map((c: any) => { return { ...c, noCharge: event.checked } });
    this.form.patchValue({ procedureCodes: this.selectedProcedureCodes });
  }

  addProcedureCode(event: MouseEvent, code: any) {
    event.preventDefault();
    event.stopPropagation();
    // Use the reusable component's addChip method
    this.procedureCodeSelector.addChip(code).then(() => {
      // After the chip is added, trigger change detection if needed
      this.cd.detectChanges();
    });
  }

  onProcedureCodesChanged(codes: ProcedureCode[]) {
    this.selectedProcedureCodes = codes;
    this.form.patchValue({ procedureCodes: codes });
  }

  // ══ RISK MANAGEMENT — workstream F of tasks/risk-management.md ═══════════════════════════
  //
  // The ward-round twin of E. A ward round never reaches the AI request detail screen — it is
  // excluded from the patient-file request list by design (B4) and navigates straight to
  // `add-note` — so if the extractor's facility issues are not offered HERE they are offered
  // nowhere, and the whole ward-round half of the feature is dead.
  //
  // F2 is gated on suggestions being PRESENT, which is naturally ward-round-only: nothing else
  // populates `suggestedRiskItems` on an editing note.

  /** F2 — the section renders only when the extractor actually found something. */
  get hasRiskSuggestions(): boolean {
    return this.riskSuggestions.length > 0;
  }

  /** F3 — dedupe feed for the shared list, mirroring E4. Patient-wide, not note-wide. */
  get addedRiskIssues(): string[] {
    return (this.riskItems || []).map((item: any) => item?.issue).filter((issue: any) => !!issue);
  }

  canAddRiskItem(issue: string): boolean {
    if (!issue) return false;
    const target = String(issue).trim().toLowerCase();
    return (this.riskItems || []).filter((item: any) => String(item?.issue || '').trim().toLowerCase() === target).length === 0;
  }

  onAddRiskSuggestion(payload: { event: MouseEvent; suggestion: RiskSuggestion }) {
    this.addRiskItem(payload.event, payload.suggestion);
  }

  /**
   * F3 — writes the SAME `PatientRiskItem` E3 writes, carrying `aiRequestId`.
   *
   * Same shared dialog, same server-side §4 resolution, same "tell the doctor what the server
   * actually decided" toast. The only differences from E3 are where the date comes from (the
   * note's own date — a ward round dictated on Tuesday about Tuesday's round) and that this
   * screen has no appointment list to feed §4 branch 2, which yields one row in the entire
   * `PatientAppointments` table anyway.
   */
  async addRiskItem(event: MouseEvent, suggestion: RiskSuggestion | null) {
    this.analyticsService.trackButtonClick('Note Add Risk Item');
    event?.preventDefault();
    event?.stopPropagation();

    if (this.addingRiskItem) return;

    // ⚠ THIS GUARD IS NOW THE ENFORCEMENT POINT FOR `patientId`, not just defensive noise.
    // User decision 2026-09-01: *"patientId should never be null (enforced by the workflows and
    // the UI, not needed on the db layer)."* The column is still nullable and the server still
    // accepts NULL, so nothing downstream will catch a risk item posted without a patient — this
    // early return is what makes it impossible from here. `this.patient` comes from
    // `currentPatient$` (this screen is inside a patient file), so the patient is genuinely in
    // context and the doctor is never asked to pick one; the only case that reaches this message
    // is a click landing before that observable has emitted.
    if (!this.patient?.id || !this.patient?.groupId) {
      this.showToastMessage('The patient is still loading. Please try again in a moment.');
      return;
    }

    if (suggestion?.issue && !this.canAddRiskItem(suggestion.issue)) {
      this.showToastMessage('That issue is already on the risk register for this patient.');
      return;
    }

    // The calendar day AT THE FACILITY (§5c) — the note's date, not today.
    const dateOfIssue = this.form.value.date ? moment(this.form.value.date) : moment();

    const dialogData: RiskItemAddDialogData = {
      suggestion,
      dateOfIssue,
      facility: predictRiskFacility({
        patient: this.patient,
        hospitals: this.hospitals,
        // No appointment list on this screen, and `appointmentId` is therefore always NULL in the
        // payload below — so §4 branch 2 cannot fire on the server either, and the preview must
        // not fire it here. The two agree by construction rather than by luck.
        appointments: null,
        appointmentId: null,
        // E3-1 — branch 3, which is THE branch for a ward round, now runs here rather than being
        // left entirely to the POST.
        admissionRecords: this.admissionRecords,
        dateOfIssue,
        groupId: this.patient.groupId,
      }),
      patientName: `${this.patient.name || ''} ${this.patient.surname || ''}`.trim() || null,
      // The doctor may correct the resolved facility. Same list `admit-details` uses, wards
      // nested; may be `null` if the fetch failed, and the dialog tolerates that.
      hospitals: this.hospitals,
    };

    const result: RiskItemAddDialogResult | null | undefined = await this.dialog
      .open(RiskItemAddDialogComponent, { data: dialogData, autoFocus: false })
      .afterClosed()
      .toPromise();

    if (!result) return;

    const encounter = pickEncounterAppointment(null, result.dateOfIssue, this.patient);

    // The SECOND `predictRiskFacility` call that used to sit here is gone. It re-ran the §4
    // preview against the final date so the post-save toast could report a client/server
    // disagreement — but the server now stores the facility this screen sends, so there is no
    // second opinion left to disagree with and nothing to reconcile.

    const riskItemData = {
      groupId: this.patient.groupId,
      patientId: this.patient.id,
      // F3 — provenance. NULL for a note that did not come from a recording, which is legal.
      aiRequestId: this.aiRequestId,
      category: result.category,
      severity: result.severity,
      dateOfIssue: moment(result.dateOfIssue).format('YYYY-MM-DD'),
      issue: result.issue,
      quote: result.quote,
      notes: result.notes,
      status: 'Open',
      appointmentId: encounter?.id ?? null,
      // The doctor's explicit facility override, or NULL when they left the preview alone — in
      // which case the server resolves §4 exactly as it always has. Validated server-side against
      // this same `groupId`; an invalid choice is a 400, never a silent re-resolve.
      facilityChoice: result.facilityChoice,
      // §28 / D15 — the photographs attached in the dialog. MUST be forwarded: this object is
      // built field by field, so omitting the key would discard every photo the doctor attached
      // while still reporting a successful save. `addRiskItem` turns a non-empty array into a
      // multipart request and leaves an empty one as the JSON it has always been.
      //
      // This is the user's own scenario end to end: the photo was taken in theatre, the doctor is
      // now writing this note, the suggestion appears, and the gallery is two taps away.
      media: result.media,
    };

    this.addingRiskItem = true;

    this.patientRiskItemService.addRiskItem(riskItemData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.addingRiskItem = false;
          this.showToastMessage(this.describeRiskResolution(response?.data));
        },
        error: (error) => {
          this.addingRiskItem = false;
          console.error('Error saving risk item: ', error);
          this.showToastMessage('There was an error adding the issue to the risk register. Please try again.');
        }
      });
  }

  /**
   * Confirms what was stored, naming the facility on the row that came back.
   *
   * THE RECONCILIATION HALF OF THIS IS GONE — see E3's twin. It compared the dialog's §4 preview
   * against the server's `resolvedBy`, which mattered while the server resolved the facility
   * itself. The client now decides and the server stores it, so the two cannot disagree.
   */
  private describeRiskResolution(item: any): string {
    if (!item) return 'Issue added to the risk register.';
    const facility = [item.hospitalName, item.wardName].filter((part: any) => !!part).join(' — ');
    const branch = riskResolutionLabel(item.resolvedBy, item.location);
    return facility
      ? `Issue added to the risk register — ${facility} (${branch}).`
      : `Issue added to the risk register — ${branch}.`;
  }

  // ══ BACKGROUND DETECTION — workstream M of tasks/risk-management.md (Phase 2) ════════════
  //
  // The instruction this implements: *"show the view button as soon as the note is ready and then
  // run this detection path in the background, inside the note-detail component we show this as
  // its own collapsible section with a loader while that background process is running."*
  //
  // The server half was already right — `GenerateWardRoundOutput` sends `sendCompletion` before
  // it runs a single detector, so the View button has always appeared first. What was missing is
  // everything below: the note had no way to know a background pass was running, no way to hear
  // it finish, and no way to tell "still running" from "found nothing" (D14).

  /**
   * BOTH sections are gated on this, and on nothing else.
   *
   * A hand-written note has no `AIRequest`, therefore no transcript, therefore no detection pass
   * and nothing to wait for. Showing it a loader — or, worse, an authoritative "no facility issues
   * were found" — would be inventing a result for a process that never ran.
   */
  get hasAiRequest(): boolean {
    return !!this.aiRequestId;
  }

  /** M5 — the ONLY state that shows a spinner. */
  get detectionPending(): boolean {
    return this.backgroundDetectionStatus === 'pending';
  }

  /**
   * M5 — the only state in which an empty section may claim nothing was found.
   *
   * `'none'` deliberately does NOT count. A request that predates the column, or one whose
   * detectors ran inline inside `GenerateAIOutput` rather than in the background, has a NULL
   * suggestion column that means "this pass never ran here" — not "it ran and found nothing".
   */
  get detectionComplete(): boolean {
    return this.backgroundDetectionStatus === 'complete';
  }

  /** M5 — both detectors threw. A PARTIAL failure is reported as `complete` by the pipeline. */
  get detectionFailed(): boolean {
    return this.backgroundDetectionStatus === 'failed';
  }

  /**
   * `'none'` — no BACKGROUND pass ran for this request. Not an error, and not an empty result.
   *
   * This is not only the legacy state. A CONSULT note carries an `aiRequestId` too (the AI request
   * detail's "add note" path writes one), and `GenerateAIOutput` runs its detectors INLINE, inside
   * the request's own `Promise.all`, before completion is ever sent — so those requests have fully
   * populated suggestion columns and a `backgroundDetectionStatus` of `'none'`, correctly, because
   * nothing about them ever ran in the background.
   *
   * Which is why results are shown on `detectionSettled` (complete OR none) and not on `complete`
   * alone: gating the list on `complete` would render an empty panel over a consult note whose
   * suggestions are sitting right there in the column.
   */
  get detectionNotRun(): boolean {
    return this.backgroundDetectionStatus === 'none';
  }

  /**
   * Nothing is in flight, so whatever is in the suggestion columns is all there is going to be.
   *
   * The distinction `complete` still carries alone, and `none` does NOT, is the right to say
   * "nothing was found" — see the empty-state gates in the template.
   */
  get detectionSettled(): boolean {
    return this.detectionComplete || this.detectionNotRun;
  }

  /** M3 — the suggested tasks, flattened to a list the template can iterate without optionals. */
  get suggestedTaskList(): any[] {
    const tasks = this.suggestedAdminTasks?.tasks;
    return Array.isArray(tasks) ? tasks : [];
  }

  get hasSuggestedTasks(): boolean {
    return this.suggestedTaskList.length > 0;
  }

  /**
   * M7 — fetch the request row on open, so a note revisited after a page refresh shows the TRUE
   * state instead of one reconstructed from socket traffic it was not present for.
   *
   * Uses `getAIRequestByIdWithoutEditing`, not `getPatientAIRequestById`: the latter pushes the
   * row into the service's `editingAIRequestSubject`, which would silently install this ward
   * round as the request the AI-request-detail screen thinks it is editing.
   */
  private loadBackgroundDetection() {
    const requestId = this.aiRequestId;

    if (!requestId) {
      // A note with no request: reset to the neutral state rather than leaving the previous
      // note's status behind, which would render a loader over an unrelated hand-written note.
      this.backgroundDetectionStatus = 'none';
      this.suggestedAdminTasks = null;
      this.detectionFetchedFor = null;
      return;
    }

    // `patchForm` runs on the route load AND again on every `editingNote$` emission. Refetching
    // each time would be pointless traffic, and — worse — a second in-flight snapshot racing the
    // first one back.
    if (this.detectionFetchedFor === requestId) return;
    this.detectionFetchedFor = requestId;

    const seq = this.detectionUpdateSeq;

    this.patientAiService.getAIRequestByIdWithoutEditing(requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const request = response?.data;
          if (!request) return;
          // The note may have been swapped while this was in flight.
          if (this.aiRequestId !== requestId) return;
          // A socket update landed while this snapshot was in flight, and a live update is by
          // definition newer than a snapshot taken before it. Applying this now would put the
          // spinner back on top of results the doctor is already looking at.
          if (this.detectionUpdateSeq !== seq) return;

          this.applyDetectionSnapshot(request);
          this.cd.detectChanges();
        },
        error: (error) => {
          // Leave the status where it is rather than guessing. A failed fetch is not evidence
          // that detection failed, and claiming so would be the same fabrication D14 forbids.
          console.error('Error loading AI request for background detection state: ', error);
          this.detectionFetchedFor = null;
        }
      });
  }

  /** M7 — a FULL row: every field is present and authoritative, including the ones that are NULL. */
  private applyDetectionSnapshot(request: any) {
    this.backgroundDetectionStatus = this.normaliseDetectionStatus(request.backgroundDetectionStatus);
    this.riskSuggestions = normaliseRiskSuggestions(request.suggestedRiskItems);
    this.suggestedAdminTasks = request.suggestedAdminTasks ?? null;
  }

  /**
   * M6 — a DELTA: `{ id, ...onlyTheFieldsThatChanged }`.
   *
   * Every field is applied only when the payload actually carries it. The pipeline emits four
   * separate updates for one ward round — `pending`, then the two suggestion columns as each
   * detector lands, then the terminal status — and treating an absent key as `null` would have
   * the risk-items update wipe the admin-tasks list that arrived a moment earlier.
   */
  private applyDetectionUpdate(update: any) {
    let applied = false;

    if (update.backgroundDetectionStatus !== undefined) {
      this.backgroundDetectionStatus = this.normaliseDetectionStatus(update.backgroundDetectionStatus);
      applied = true;
    }
    if (update.suggestedRiskItems !== undefined) {
      this.riskSuggestions = normaliseRiskSuggestions(update.suggestedRiskItems);
      applied = true;
    }
    if (update.suggestedAdminTasks !== undefined) {
      this.suggestedAdminTasks = update.suggestedAdminTasks ?? null;
      applied = true;
    }

    // Only a payload that actually touched detection invalidates an in-flight M7 snapshot. The
    // stream carries every `aiRequestUpdated` for the group, and an unrelated field changing on
    // this same request must not cause the snapshot to be thrown away.
    if (applied) this.detectionUpdateSeq++;
  }

  /**
   * The column is NOT NULL in the database under `DF_AIRequests_backgroundDetectionStatus`, so a
   * value outside the vocabulary can only mean the row was written by something that does not
   * know about D14. Collapsing that to `'none'` fails to the quiet state — no loader, no empty
   * state, no claim — rather than to a spinner that would never stop.
   */
  private normaliseDetectionStatus(value: any): string {
    const status = String(value ?? 'none');
    return ['none', 'pending', 'complete', 'failed'].includes(status) ? status : 'none';
  }

  // ── M8 — adding a suggested task, through the EXISTING task path ────────────────────────
  //
  // Byte-for-byte the flow `patient-ai-request-detail.addTask` and `discharge-details.addTask`
  // use, down to the dialog and the `status: 'Pending'`: same dialog, same service, same
  // `PatientTask`. A ward round's tasks are not a different kind of task.

  canAddTask(task: string): boolean {
    if (!task) return false;
    const target = String(task).trim().toLowerCase();
    return (this.patientTasks || [])
      .filter((t: any) => String(t?.task || '').trim().toLowerCase() === target).length === 0;
  }

  openAddTaskDialog(content?: any): Promise<TaskDialogResult | null | undefined> {
    const data: TaskDialogData = content
      ? { date: moment(content.date), task: content.task, dueDate: content.dueDate || '' }
      : {};
    return this.dialog.open<TaskDialogComponent, TaskDialogData, TaskDialogResult | null>(TaskDialogComponent, { width: '600px', data })
      .afterClosed().toPromise();
  }

  async addTask(event: MouseEvent, taskValue?: any) {
    this.analyticsService.trackButtonClick('Note Add Suggested Task');
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.patient?.id || !this.patient?.groupId) {
      this.showToastMessage('The patient is still loading. Please try again in a moment.');
      return;
    }

    const dialogResult = await this.openAddTaskDialog({ date: moment(), task: taskValue?.task || '' });

    if (!dialogResult) return;

    const { date, task, notes, dueDate, assigneeUserIds } = dialogResult;

    if (!date || !task) {
      this.showToastMessage('Date and Task are required to add a task');
      return;
    }

    const taskData = {
      patientId: this.patient.id,
      groupId: this.patient.groupId,
      date: moment.utc(date).startOf('day').toISOString(),
      dueDate: moment.utc(dueDate).startOf('day').toISOString(),
      task,
      notes,
      assigneeUserIds,
      status: 'Pending'
      // NOTE: no `aiRequestId`. M8 asked for one on both writes, but `PatientTasks` has no such
      // column — 12 columns, none of them a request link (verified against `sys.columns` and
      // `models/patient-task.js`). Sequelize silently DROPS a key that is not a model attribute,
      // so sending one would look like provenance and store nothing. Reported rather than faked;
      // the risk item below does carry it, because `PatientRiskItems.aiRequestId` exists.
    };

    this.taskService.addTask(taskData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showToastMessage('Patient task saved successfully');
        },
        error: (error) => {
          console.error('Error saving patient task: ', error);
          this.showToastMessage('There was an error saving the patient task. Please try again.');
        }
      });
  }

  resetForm() {
    setTimeout(() => {
      this.editingNote = null;
      this.recordingJobId = null;
      // F1 — cleared with the note. Leaving these behind would stamp the previous ward round's
      // AIRequest onto the next note, and show its suggestions under it.
      this.aiRequestId = null;
      this.riskSuggestions = [];
      // M5 — same reasoning. A stale `pending` here would spin forever over a blank new note,
      // and a stale `complete` would tell the doctor the previous ward round's empty result was
      // this note's.
      this.backgroundDetectionStatus = 'none';
      this.suggestedAdminTasks = null;
      this.detectionFetchedFor = null;
      this.selectedProcedureCodes = [];
      this.noteMediaObjects = [];
      this.loadedNoteMediaObjects = [];
      this.mediaPatchedFor = null;
      this.form.reset();
      this.initForm();
      this.applyChatPrefill();
      this.cd.detectChanges();
    });
  }

  /** Opened from a chat action: the chat's text as the note (its first line as the title), and the conversation. */
  private applyChatPrefill(): void {
    const prefill = notePrefill(this.route.snapshot.queryParamMap);
    this.chatConversationId = prefill.chat;
    this.chatNoteText = prefill.text;
    if (prefill.text) this.form.patchValue({ notes: prefill.text, title: noteCardTitle(prefill.text) });
  }

  /** A note made from a chat action goes back to that conversation as a card, with its type, from the person who saved it. */
  private postChatCard(result: any): void {
    const noteType = this.noteTypes.find((nt: any) => nt.id === this.form.value.noteType)?.label ?? null;
    const card = this.chatConversationId ? chatNoteCard(result?.data, this.patient, this.form.value.notes ?? '', noteType) : null;
    if (!card) return;
    this.chat.store.sendAction(this.chatConversationId!, card).catch((e: unknown) => {
      console.error('Error posting the note to the chat: ', e);
      this.showToastMessage('The note was saved, but could not be posted to the chat.');
    });
  }

  onNoteMediaUpload(files: FileObject[]) {
    const newMediaObjects = files.map(file => ({
      ...file,
      //url: URL.createObjectURL(file),
      type: file.type,
    }));

    // Check for duplicates and avoid appending unnecessarily
    const uniqueMediaObjects = newMediaObjects.filter(newObj =>
      !this.noteMediaObjects.some(existingObj => existingObj.url === newObj.url)
    );

    this.noteMediaObjects = [...this.noteMediaObjects, ...uniqueMediaObjects];
  }

  onFilesEdited(files: any) {
    this.noteMediaObjects = files as FileObject[];
  }

  onNoteMediaDelete(fileObject: FileObject) {
    const index = this.noteMediaObjects.findIndex(obj => obj.url === fileObject.url);
    if (index > -1) {
      this.noteMediaObjects.splice(index, 1);
      if (fileObject.id) {
        this.noteService.deleteNoteMedia(fileObject.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.feedbackService.showToast(`Note media deleted successfully.`);
            },
            error: (error) => {
              this.feedbackService.showToast(`There was an error deleting the note media. Please try again.`);
            }
          });
      }
    }
  }

  saveNote() {
    const existingMediaFiles = this.editingNote?.media || [];
    const newMediaFiles = this.noteMediaObjects.filter(obj => !obj.id);

    const date: moment.Moment = this.form.value.date;

    const noteData = {
      ...this.form.value,
      existingMedia: existingMediaFiles,
      procedureCodes: this.selectedProcedureCodes,
      date: date.utc().startOf('day').toISOString(),
      recordingJobId: this.recordingJobId,
      // F3 — the note's provenance. Ward rounds have an `AIRequest` since D1, so a ward-round
      // note can finally point back at the transcript it came from.
      //
      // SPREAD ONLY WHEN PRESENT, deliberately. `PatientNote.aiRequestId` is written by passing
      // the client object straight to Sequelize (`existingNote.update(data)` on the update side,
      // `PatientNote.create(noteData)` in the CreatePatientNote function on the create side), so
      // an explicit `aiRequestId: null` in the payload would CLEAR the link on any note that
      // reached this screen without one on the form — provenance is append-only here.
      ...(this.aiRequestId ? { aiRequestId: this.aiRequestId } : {})
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(noteData));


    const mediaAppendPromises = newMediaFiles.map((obj, index) => {
      return fetch(obj.url)
        .then(response => response.blob())
        .then(blob => {
          formData.append(`newMedia${index}`, blob, `${moment().unix()}.${obj.type.split('/')[1]}`);
        });
    });

    Promise.all(mediaAppendPromises)
      .then(() => {
        if (this.editingNote && this.editingNote.id) {
          this.noteService.updateNote(this.editingNote.id, formData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (result) => {
                this.showToastMessage('Patient note updated successfully');
                this.returnToParent();
              },
              error: (error) => {
                console.error('Error updating patient note: ', error);
                this.showToastMessage('There was an error updating the patient note. Please try again.');
              }
            });
        } else {
          this.noteService.addNote(formData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (result) => {
                this.showToastMessage('Patient note saved successfully');
                this.postChatCard(result);
                this.returnToParent();
              },
              error: (error) => {
                console.error('Error saving patient note: ', error);
                this.showToastMessage('There was an error saving the patient note. Please try again.');
              }
            });
        }
      });
  }


  async deleteNote() {
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;

    this.noteService.deleteNote(this.editingNote.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient note deleted successfully');
          this.returnToParent()
        },
        error: (error) => {
          console.error('Error saving patient note: ', error);
          this.showToastMessage('There was an error deleting the patient note. Please try again.');
        }
      });
  }

  returnToParent() {
    if (this.patient?.id) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
    } else {
      let parentPath = 'list';
      const currentUrl = this.router.url;
      if (currentUrl.includes('schedule')) {
        parentPath = 'schedule';
      }
      if (currentUrl.includes('filters')) {
        parentPath = 'filters';
      }
      if (currentUrl.includes('coding')) {
        parentPath = 'coding';
      }
      this.router.navigate(['/patients', { outlets: { patient: null } }]).then(() => {
        this.router.navigate(['/patients', parentPath]);
      });
    }
  }

  noteScanFileObjects: FileObject[] = [];
  noteScanUrl: string = '';
  noteScanProcessing: boolean = false;

  openAddUsingImageDialog() {
    // Always false if premium, otherwise check token balance
    if (this.isPremium) {
      this.lockProcessing = false;
    } else if (this.usageService.tokenBalanceSnapshot < 0) {
      this.lockProcessing = true;
    } else {
      this.lockProcessing = false;
    }
    this.dialog.open(this.scanNoteDetailsDialog!, NOTE_SCAN_DIALOG_CONFIG);
  }

  private resetNoteScanning() {
    this.noteScanFileObjects = [];
    this.noteScanUrl = '';
    this.noteScanProcessing = false;
  }

  onNoteScanUpload(files: FileObject[]) {
    this.resetNoteScanning();

    if (files.length > 0) {
      this.noteScanFileObjects = [{
        ...files[0],
        ///url: URL.createObjectURL(files[0]),
        type: 'image'
      }];
      this.uploadImage();
    }
  }

  onNoteScanDelete(fileObject: FileObject) {
    this.resetNoteScanning();
  }

  async uploadImage() {
    this.analyticsService.trackButtonClick(`Note Details Image Extraction`);
    this.noteScanProcessing = true;
    const formData = new FormData();
    if (this.noteScanFileObjects.length > 0) {
      formData.append('image', await this.blobToFile(this.noteScanFileObjects[0].url, `${moment().valueOf()}.${this.noteScanFileObjects[0].type}`));
    }

    this.aiService.uploadImage(formData)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((result: any) => {
          this.noteScanUrl = result.imageUrl;
          return this.aiService.extractNoteDetailsFromImage(this.groupId, this.noteScanUrl, false);
        })
      )
      .subscribe({
        next: (result: any) => {
          this.resetNoteScanning();
          if (result.data.date) {
            result.data.date = moment.utc(result.data.date, 'DD/MM/YYYY').startOf('day');
          }

          //const noteType = this.noteTypeService.getWardRoundNoteType();
          const note = {
            date: result.data.date,
            title: result.data.title || 'Ward Round',
            notes: result.data.notes,
            // detectedNoteType: noteType
          }

          this.editingNote = note;
          this.patchForm();
          this.noteScanProcessing = false;
          this.dialog.closeAll();
        },
        error: (error) => {
          this.resetNoteScanning();
          this.noteScanProcessing = false;
          console.error(error);
          this.feedbackService.showToast('There was an error extracting information from the provided image. Please try again.');
        }
      });
  }

  private async blobToFile(url: string, defaultFileName: string) {
    const response = await fetch(url);
    const blob = await response.blob();
    const contentType = blob.type;

    let extension = '';
    if (contentType === 'image/png') {
      extension = '.png';
    } else if (contentType === 'image/jpeg' || contentType === 'image/jpg') {
      extension = '.jpg';
    } else {
      throw new Error('Unsupported image format: ' + contentType);
    }

    const fileName = defaultFileName.replace(/\.[^/.]+$/, extension); // Replace the default extension with the correct one
    return new File([blob], fileName, { type: contentType });
  }


}
